/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  InputJobInfoMultipleCharge,
  JobIdAndRequestedEndDatetimesInfo,
  JobIdInfo,
  SelectTaskJobInfoMultipleCharge,
  SelectTaskListMultipleCharge,
  UpdateTaskDataMultipleCharge,
} from "client";
import { JobInputItem } from "../JobInputCard";
import { MultipleChargeJobsetInputItem } from "../MultipleChargeSettingPanel";
import { MultipleChargeTasksItem } from "components/TaskRegisterMultipleCharge/hooks/GetMultipleChargeTasksHook";
import { AwvJobItem } from "../AwvJobDisplay";
import { validateFromTo } from "utils/DateTime";
import { TaskControlMode } from "types";

/**
 * Validate input jobs
 *
 * @param {JobInputItem[]} job
 * @return {boolean} true: valid / false: invalid
 */
export const validateJobInput = (job: JobInputItem) => {
  return (
    job.materialType !== undefined &&
    job.materialQuantity !== undefined &&
    job.materialQuantity > 0 &&
    job.loadingNodeId !== undefined &&
    // job.extensionTimeLoad !== undefined &&
    job.loadingUserId !== undefined &&
    job.unloadingNodeId !== undefined &&
    // job.extensionTimeUnload !== undefined &&
    job.unloadingUserId !== undefined &&
    validateFromTo(
      job.requestedEndDatetimeFrom,
      job.requestedEndDatetimeTo
    ).every((result) => result)
  );
};

/**
 * Validate input jobs
 *
 * @param {JobInputItem[]} jobs
 * @return {boolean} true: valid / false: invalid
 */
export const validateJobInputs = (jobs: JobInputItem[]) => {
  return jobs.every((job) => validateJobInput(job));
};

/**
 * Validate input jobset
 *
 * @param {MultipleChargeJobsetInputItem} jobset
 * @return {boolean} true: valid / false: invalid
 */
export const validateJobsetInput = (jobset: MultipleChargeJobsetInputItem) => {
  return (
    jobset.jobsetName !== undefined &&
    jobset.jobsetName.length > 0 &&
    jobset.startNodeId !== undefined &&
    jobset.endNodeId !== undefined &&
    validateFromTo(jobset.startDate, jobset.endDate).every(
      (result) => result
    ) &&
    jobset.memo !== undefined &&
    jobset.memo.length > 0 &&
    jobset.frameTypeAndNumbers.length > 0 &&
    jobset.chargerIds.length > 0
  );
};

/**
 * Validate and Edit input data
 *
 * @param {JobInputItem[]} jobs
 * @param {JobInputItem[]} addJobInfos
 * @param {JobIdAndRequestedEndDatetimesInfo[]} updateJobInfos
 * @param {JobIdInfo[]} deleteJobInfos
 * @param {UpdateTaskDataMultipleCharge} jobset
 * @return {boolean} true: valid / false: invalid
 */
export const validateAndEditJobset = (
  jobs: JobInputItem[],
  addJobInfos: JobInputItem[],
  updateJobInfos: JobIdAndRequestedEndDatetimesInfo[],
  deleteJobInfos: JobIdInfo[],
  jobset: UpdateTaskDataMultipleCharge
) => {
  if (validateJobInputs(jobs)) {
    // TransForm addJobInfos from array of JobInputItem to array of InputJobInfoMultipleCharge.
    jobset.addJobInfos = transformAddJobInfos(addJobInfos);
    jobset.updateJobInfos = updateJobInfos;
    jobset.deleteJobInfos = deleteJobInfos;
    return true;
  } else {
    return false;
  }
};

/**
 * Create JobInfos for register
 *
 * @param {JobInputItem[]} jobs Input data
 * @return {InputJobInfoMultipleCharge[]} JobInfos
 */
export const createRegisterJobInfos = (jobs: JobInputItem[]) => {
  return jobs.map((job) => {
    return {
      unloadingUserId: job.unloadingUserId,
      unloadingNodeId: job.unloadingNodeId,
      loadingUserId: job.loadingUserId,
      loadingNodeId: job.loadingNodeId,
      requestedEndDatetimes: [
        {
          endFrom: job.requestedEndDatetimeFrom,
          endTo: job.requestedEndDatetimeTo,
        },
      ],
      loadingMaterialId: job.materialType,
      loadingQuantity: job.materialQuantity,
    } as InputJobInfoMultipleCharge;
  });
};

/**
 * Aggregates job info by AWV
 *
 * @param {MultipleChargeTasksItem} tasks task data
 * @return {AwvJobItem[]} aggregated jobs
 */
export const aggregateAwvJobs = (tasks: MultipleChargeTasksItem) => {
  const awvJobs: AwvJobItem[] = [];
  tasks.tasks.forEach((task) => {
    const awvInfo = awvJobs.find(
      (item) => item.frameTypeNumber === task.frameTypeNumber
    );
    if (awvInfo === undefined) {
      awvJobs.push({
        frameTypeNumber: task.frameTypeNumber,
        jobs: task.jobs,
      });
    } else {
      awvInfo.jobs = [...awvInfo.jobs, ...task.jobs];
    }
  });
  return awvJobs;
};

/**
 * Get Job input items from input data
 *
 * @param {InputJobInfoMultipleCharge[]} jobInfos Job input data
 * @param {TaskControlMode} mode mode
 * @return {JobInputItem[]} Job input items
 */
export const getInitialJobInputItems = (
  jobInfos: SelectTaskJobInfoMultipleCharge[],
  mode: TaskControlMode
) => {
  return jobInfos.length > 0
    ? jobInfos.map((jobInfo, index) => {
        return {
          jobId: jobInfo.jobId,
          extensionTimeLoad: jobInfo.extensionTimeLoad,
          extensionTimeUnload: jobInfo.extensionTimeUnload,
          loadingNodeId: jobInfo.loadingNodeId,
          loadingUserId: jobInfo.loadingUserId,
          materialQuantity: jobInfo.loadingQuantity,
          materialType: jobInfo.loadingMaterialId,
          mode: mode,
          requestedEndDatetimeFrom:
            jobInfo.jobRequestedEndDatetimes.length > 0
              ? jobInfo.jobRequestedEndDatetimes[0].endFrom
              : undefined,
          requestedEndDatetimeTo:
            jobInfo.jobRequestedEndDatetimes.length > 0
              ? jobInfo.jobRequestedEndDatetimes[0].endTo
              : undefined,
          unloadingNodeId: jobInfo.unloadingNodeId,
          unloadingUserId: jobInfo.unloadingUserId,
        } as JobInputItem;
      })
    : [createInitialJoInputItem(0)];
};

/**
 * Create initial object for job input
 *
 * @param {number} jobId Job ID
 * @return {JobInputItem} Created object.
 */
export const createInitialJoInputItem = (jobId: number): JobInputItem => {
  return {
    jobId: jobId,
    materialType: undefined,
    materialQuantity: undefined,
    loadingNodeId: undefined,
    extensionTimeLoad: undefined,
    loadingUserId: undefined,
    unloadingNodeId: undefined,
    extensionTimeUnload: undefined,
    unloadingUserId: undefined,
    requestedEndDatetimeFrom: new Date(),
    requestedEndDatetimeTo: new Date(),
    mode: "register",
  };
};

/**
 * Get Jobset input item from input data
 *
 * @param {RegisterTaskDataMultipleCharge} jobset Jobset input data
 * @return {MultipleChargeJobsetInputItem} Jobset input item
 */
export const getInitialJobsetInputItem = (
  jobset: SelectTaskListMultipleCharge
) => {
  return {
    jobsetId: jobset.jobSetId,
    jobsetName: jobset.jobSetName,
    startNodeId: jobset.startNodeId >= 0 ? jobset.startNodeId : undefined,
    endNodeId: jobset.endNodeId >= 0 ? jobset.endNodeId : undefined,
    startDate: jobset.jobsetStartDatetime,
    endDate: jobset.jobsetEndDatetime,
    memo: jobset.memo,
    frameTypeAndNumbers: jobset.frameTypeAndNumbers,
    chargerIds: jobset.chargerIds,
    moveToStart: jobset.moveToStart,
    resetJobRequestedEndDatetime: jobset.resetJobRequestedEndDatetime,
    chargeOutsideDelivery: jobset.chargeOutsideDelivery,
    versionId: jobset.versionId,
  } as MultipleChargeJobsetInputItem;
};

/**
 * Create JobInfos for register
 *
 * @param {JobInputItem[]} jobs Input data
 * @return {InputJobInfoMultipleCharge[]} JobInfos
 */
export const transformAddJobInfos = (jobs: JobInputItem[]) => {
  return jobs.map((job) => {
    return {
      unloadingUserId: job.unloadingUserId,
      unloadingNodeId: job.unloadingNodeId,
      extensionTimeUnload: job.extensionTimeUnload,
      loadingUserId: job.loadingUserId,
      loadingNodeId: job.loadingNodeId,
      extensionTimeLoad: job.extensionTimeLoad,
      requestedEndDatetimes: [
        {
          endFrom: job.requestedEndDatetimeFrom,
          endTo: job.requestedEndDatetimeTo,
        },
      ],
      loadingMaterialId: job.materialType,
      loadingQuantity: job.materialQuantity,
    } as InputJobInfoMultipleCharge;
  });
};

/**
 * Convert Jobinfo objects
 *
 * @param {SelectTaskJobInfoMultipleCharge[]} jobInfos Input objects
 * @return {InputJobInfoMultipleCharge[]} Converted objects
 */
export const transformSelectTaskJobInfosToInputJobInfos = (
  jobInfos: SelectTaskJobInfoMultipleCharge[]
): InputJobInfoMultipleCharge[] => {
  return jobInfos.map((jobInfo) => {
    return {
      unloadingUserId: jobInfo.unloadingUserId,
      unloadingNodeId: jobInfo.unloadingNodeId,
      extensionTimeUnload: jobInfo.extensionTimeUnload,
      loadingUserId: jobInfo.loadingUserId,
      loadingNodeId: jobInfo.loadingNodeId,
      extensionTimeLoad: jobInfo.extensionTimeLoad,
      requestedEndDatetimes: jobInfo.jobRequestedEndDatetimes,
      loadingMaterialId: jobInfo.loadingMaterialId,
      loadingQuantity: jobInfo.loadingQuantity,
    };
  });
};
